import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getWeatherForDate, WeatherData } from '../utils/weather';
import { useAuth } from '../contexts/AuthContext';
import { useUserLocation } from '../hooks/useUserLocation';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { calculateDistance, formatDistance } from '../utils/distance';
import { parseDateInCST } from '../utils/dateUtils';
import { cleanText } from '../utils/textUtils';
import FavoriteButton from './FavoriteButton';
import ShareButton from './ShareButton';
import './EventCard.css';

interface Event {
  id: string;
  slug: string;
  title: string;
  short_description: string;
  category: string;
  age_min: number;
  age_max: number;
  venue_name: string;
  address?: string;
  city: string;
  latitude?: number;
  longitude?: number;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  is_free: boolean;
  price_description?: string;
  rating_avg?: number;
  rating_count?: number;
  registration_url?: string;
}

interface EventCardProps {
  event: Event;
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const [selectedCity, setSelectedCity] = useState<string>('Houston');
  const { location: userLocation } = useUserLocation(selectedCity, { requestGeolocation: false });
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    if (preferences?.city) {
      setSelectedCity(preferences.city);
    } else {
      const savedCity = localStorage.getItem('selectedCity');
      if (savedCity) {
        setSelectedCity(savedCity);
      }
    }
  }, [preferences]);

  useEffect(() => {
    getWeatherForDate(event.start_date, event.city).then(setWeather);
  }, [event.start_date, event.city]);

  const getCategoryEmoji = (category: string) => {
    const emojiMap: { [key: string]: string } = {
      'Arts': '🎨',
      'Sports': '⚽',
      'STEM': '🔬',
      'Outdoors': '🌳',
      'Performing Arts': '🎭',
      'Library': '📚',
      'Music': '🎵',
    };
    return emojiMap[category] || '🎉';
  };

  const getDistanceToEvent = (): string | null => {
    if (!user || !userLocation || !event.latitude || !event.longitude) return null;
    const distance = calculateDistance(
      userLocation.lat,
      userLocation.lon,
      event.latitude,
      event.longitude
    );
    return formatDistance(distance);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return 'Ongoing';
    }
    
    try {
      const date = parseDateInCST(dateString);
      
      // Check if date is invalid
      if (isNaN(date.getTime())) {
        return 'Ongoing';
      }
      
      // If date is more than 5 years in the future, it's a placeholder for ongoing items
      const fiveYearsFromNow = new Date();
      fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);
      if (date > fiveYearsFromNow) {
        return 'Ongoing';
      }
      
      const nowInCentral = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
      const todayInCentral = new Date(nowInCentral.getFullYear(), nowInCentral.getMonth(), nowInCentral.getDate());
      const tomorrowInCentral = new Date(todayInCentral);
      tomorrowInCentral.setDate(tomorrowInCentral.getDate() + 1);

      const eventDateStr = date.toLocaleDateString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      const todayStr = todayInCentral.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      const tomorrowStr = tomorrowInCentral.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Chicago'
      };

      // Build time range string
      let timeRange = '';
      if (event.start_time) {
        const startTimeStr = new Date(`2000-01-01T${event.start_time}`).toLocaleTimeString('en-US', timeOptions);
        if (event.end_time) {
          const endTimeStr = new Date(`2000-01-01T${event.end_time}`).toLocaleTimeString('en-US', timeOptions);
          timeRange = ` ${startTimeStr} - ${endTimeStr}`;
        } else {
          timeRange = ` ${startTimeStr}`;
        }
      }

      if (eventDateStr === todayStr) {
        return `Today${timeRange}`;
      } else if (eventDateStr === tomorrowStr) {
        return `Tomorrow${timeRange}`;
      } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Chicago'
      }) + timeRange;
    }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Ongoing';
    }
  };

  return (
    <Link to={`/event/${event.slug}`} className="event-card-link">
      <div className="event-card">
        <div className="favorite-button-wrapper">
          <FavoriteButton itemId={event.id} itemType="event" size="small" />
        </div>
        <div className="event-card-layout">
          <div className="event-left-section">
            <div className="event-category">
              <span className="category-emoji">{getCategoryEmoji(event.category)}</span>
              <span className="category-name">{event.category}</span>
            </div>
            <h3 className="event-title">{event.title}</h3>
            {event.short_description && (
              <p className="event-description">{cleanText(event.short_description)}</p>
            )}
          </div>
          <div className="event-right-section">
            {event.registration_url && (
              <button 
                className="action-button icon-only"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(event.registration_url, '_blank', 'noopener,noreferrer');
                }}
                title="Visit Website"
                aria-label="Visit Website"
              >
                🌐
              </button>
            )}
            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <ShareButton
                title={event.title}
                description={event.short_description}
                url={`${window.location.origin}/event/${event.slug}`}
                date={formatDate(event.start_date)}
                location={`${event.venue_name}, ${event.city}`}
                size="small"
              />
            </div>
          </div>
        </div>
        <div className="event-details">
          <div className="detail-row">
            <span className="detail-icon">👶</span>
            <span>Ages {event.age_min}-{event.age_max}</span>
          </div>
          <div className="detail-row">
            <span className="detail-icon">📅</span>
            <span>{formatDate(event.start_date)}</span>
          </div>
          <div className="detail-row">
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                event.address || `${event.venue_name}, ${event.city}, TX`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="location-link"
              onClick={(e) => e.stopPropagation()}
            >
              📍 {event.venue_name}
            </a>
          </div>
          <div className="detail-row">
            <span className="detail-icon">💵</span>
            <span>{event.is_free ? 'FREE' : event.price_description || 'Paid'}</span>
            {weather && (
              <span className="weather-info" title={weather.description}>
                {weather.icon} {weather.temperature}°F
              </span>
            )}
            {getDistanceToEvent() && (
              <span className="event-distance">🎯 {getDistanceToEvent()}</span>
            )}
          </div>
          {event.rating_avg && event.rating_count && (
            <div className="detail-row rating">
              <span className="detail-icon">⭐</span>
              <span>{event.rating_avg.toFixed(1)} ({event.rating_count} reviews)</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
